import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { Button, InputBoxSingle } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useBettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { useVirtualKeyboardVisible } from '../hooks/useVirtualKeyboardVisible';
import { updateBettieMoodSelection } from '@pocapan/shared/domains/bettie/services/bettieService';
import { attachJosa, parseMemberCalledMessage } from '@pocapan/shared/utils/bettieUtils';
import { useGetBettieChatRoomPreferences } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieChatRoomPreferences';
import PulsingGradientCircle from './PulsingGradientCircle';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { HorizontalSpeechBallon } from '../../../components/drawer/BettieNicknameUpdateBottomDrawer';
import SettingBettieSpeechStyle from './SettingBettieSpeechStyle';
import { CircleDot } from './CircleDot';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { components } from '@pocapan/shared/types/schema';
import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

const MOOD_KEYWORDS = {
  personality: [
    t('BettieSetting.moodKeyWords.personality.chicAndWarm'),
    t('BettieSetting.moodKeyWords.personality.calm'),
    t('BettieSetting.moodKeyWords.personality.playful'),
    t('BettieSetting.moodKeyWords.personality.tsundere'),
    t('BettieSetting.moodKeyWords.personality.puppyCharm')
  ],
  romanceStyle: [
    t('BettieSetting.moodKeyWords.romanticStyle.expressive'),
    t('BettieSetting.moodKeyWords.romanticStyle.moderate'),
    t('BettieSetting.moodKeyWords.romanticStyle.subtle'),
    t('BettieSetting.moodKeyWords.romanticStyle.notTooMuch'),
    t('BettieSetting.moodKeyWords.romanticStyle.natural')
  ],
  relationship: [
    t('BettieSetting.moodKeyWords.relationship.justFriends'),
    t('BettieSetting.moodKeyWords.relationship.closeFriends'),
    t('BettieSetting.moodKeyWords.relationship.flirty'),
    t('BettieSetting.moodKeyWords.relationship.complicated'),
    t('BettieSetting.moodKeyWords.relationship.special')
  ]
};

const MOOD_KEYWORDS_FOREIGN: Record<'personality' | 'romanceStyle' | 'relationship', string> = {
  personality: 'What kind of person do you think I am?',
  romanceStyle: 'How much affection do you like?',
  relationship: 'What kind of relationship do we have?'
};

const KEY_ORDER: ('personality' | 'romanceStyle' | 'relationship')[] = [
  'personality',
  'romanceStyle',
  'relationship'
];

interface BettieMoodSuggestionProps {
  setCurrentStep: (step: BettieSettingStep) => void;
  bettiePersona: ApiV1MemberPersona;
  chatRoomUuid: string;
  isForeignUser: boolean;
}

const BettieMoodSuggestion = ({
  setCurrentStep,
  bettiePersona,
  chatRoomUuid,
  isForeignUser
}: BettieMoodSuggestionProps) => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { queryParams } = useQueryParams();
  const isChangeMood = queryParams.get('isChangeMood') === 'true';

  const [currentMoodKeyword, setCurrentMoodKeyword] = useState<
    'personality' | 'romanceStyle' | 'relationship' | 'speechStyle'
  >('personality');
  const [moodInput, setMoodInput] = useState({
    relationship: '',
    personality: '',
    romanceStyle: ''
  });
  const [suggestionMoodKeyword, setSuggestionMoodKeyword] = useState<string>(() => {
    if (currentMoodKeyword === 'speechStyle') return '';
    const list = MOOD_KEYWORDS[currentMoodKeyword];
    return list[Math.floor(Math.random() * list.length)];
  });

  const [isTypingDone, setIsTypingDone] = useState(false);
  const prevSuggestionRef = useRef<string | undefined>(undefined);
  const didHydrateFromPreferencesRef = useRef(false);

  const { bettieSettingInfo } = useBettieSettingInfo();
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const moodSelectionMessage = bettiePersona?.moodSelectionMessage || [];

  const { data: chatRoomPreferences } = useGetBettieChatRoomPreferences(
    isChangeMood ? chatRoomUuid : ''
  );

  // isChangeMood 진입 시: 기존에 저장된 컨텍스트 값을 기본값으로 1회만 주입
  useEffect(() => {
    if (!isChangeMood) return;
    if (!chatRoomPreferences) return;
    if (didHydrateFromPreferencesRef.current) return;

    didHydrateFromPreferencesRef.current = true;
    setMoodInput((prev) => ({
      relationship: prev.relationship || chatRoomPreferences.relationship || '',
      personality: prev.personality || chatRoomPreferences.personality || '',
      romanceStyle: prev.romanceStyle || chatRoomPreferences.romanceStyle || ''
    }));
  }, [chatRoomPreferences, isChangeMood]);

  const handleNextClick = async () => {
    if (currentMoodKeyword === 'speechStyle') return;
    const currentIndex = KEY_ORDER.indexOf(currentMoodKeyword);
    if (currentIndex === KEY_ORDER.length - 1) {
      if (isChangeMood) {
        try {
          const requestBody = {
            context: {
              relationship: moodInput.relationship,
              personality: moodInput.personality,
              romance_style: moodInput.romanceStyle
            }
          };
          const response = await updateBettieMoodSelection(chatRoomUuid, requestBody);
          if (response.chatRoom.chatRoomId) {
            setCurrentStep('complete');
          }
        } catch {
          // no-op
        }
        return;
      }

      // 마지막 스텝(relationship) 완료 후 speechStyle로 이동
      setCurrentMoodKeyword('speechStyle');
    } else {
      setCurrentMoodKeyword(KEY_ORDER[currentIndex + 1]);
    }
  };

  useEffect(() => {
    if (currentMoodKeyword === 'speechStyle') return;
    setIsTypingDone(false);
    const pickNext = () => {
      const list = MOOD_KEYWORDS[currentMoodKeyword];
      if (!list || list.length === 0) return;
      setSuggestionMoodKeyword((prev) => {
        if (list.length <= 1) {
          return list[0];
        }
        let next = list[Math.floor(Math.random() * list.length)];
        if (next === prev) {
          const idx = (list.indexOf(next) + 1) % list.length;
          next = list[idx];
        }
        return next;
      });
    };
    pickNext();
    const id = setInterval(pickNext, 2500);
    return () => clearInterval(id);
  }, [currentMoodKeyword]);

  useEffect(() => {
    if (
      prevSuggestionRef.current !== undefined &&
      prevSuggestionRef.current !== suggestionMoodKeyword
    ) {
    }
    prevSuggestionRef.current = suggestionMoodKeyword;
  }, [suggestionMoodKeyword]);

  const handleTypingComplete = useCallback(() => {
    setIsTypingDone(true);
  }, []);

  const handlePreviousClick = () => {
    if (currentMoodKeyword === 'speechStyle') {
      setCurrentMoodKeyword('relationship');
      return;
    }
    if (KEY_ORDER.indexOf(currentMoodKeyword) === 0) {
      navigate('/bettie-chat-list', { replace: true });
      return;
    }
    setCurrentMoodKeyword(KEY_ORDER[KEY_ORDER.indexOf(currentMoodKeyword) - 1]);
  };

  const handleSpeechStyleBack = () => {
    setCurrentMoodKeyword('relationship');
  };

  if (currentMoodKeyword === 'speechStyle') {
    // speechStyle 스텝일 때 SettingBettieSpeechStyle 컴포넌트 렌더링
    return (
      <SettingBettieSpeechStyle
        setCurrentStep={setCurrentStep}
        bettiePersona={bettiePersona}
        chatRoomUuid={chatRoomUuid}
        isForeignUser={isForeignUser}
        otherMoodInputs={{
          relationship: moodInput.relationship,
          personality: moodInput.personality,
          romanceStyle: moodInput.romanceStyle
        }}
        onBack={handleSpeechStyleBack}
      />
    );
  }

  return (
    <div className="flex flex-col bg-background-light dark:bg-background-dark">
      <div className="fixed top-0 left-0 right-0 z-10 flex justify-between px-[16px] pt-[calc(env(safe-area-inset-top)+10px)] pb-[10px] bg-background-light dark:bg-background-dark">
        <ArrowLeft size={24} className="text-text-main-lv1" onClick={handlePreviousClick} />
        {!isChangeMood && (
          <button
            type="button"
            className="text-[16px] font-medium text-text-main-lv4 leading-[19px]"
            onClick={() => setCurrentStep('complete')}
          >
            {t('BettieSetting.moodSuggestion.skip')}
          </button>
        )}
      </div>
      <div className="flex flex-col items-start px-[24px] py-[10px] mt-[44px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
          {isChangeMood
            ? t('BettieSetting.moodSuggestion.changeMoodTitle', {
                nickname: attachJosa(bettieSettingInfo.bettieNickname, '과/와')
              })
            : t('BettieSetting.moodSuggestion.title', {
                nickname: attachJosa(bettieSettingInfo.bettieNickname, '이/가')
              })}
        </h1>
      </div>
      <div className="mt-[24px] px-[24px]">
        <div className="flex justify-center gap-[20px] items-center">
          <PulsingGradientCircle
            gradientCircleSize="56px"
            glassCircleSize="56px"
            showDottedCircle={false}
            blur="5.2px"
          >
            <ProfileAvatar
              thumbnailUrl={bettieSettingInfo.dataUrl || ''}
              widthAndHeight="53px"
              svgWidthAndHeight="53px"
            />
          </PulsingGradientCircle>
          <HorizontalSpeechBallon
            enableTyping={true}
            text={parseMemberCalledMessage(
              moodSelectionMessage?.[
                currentMoodKeyword as 'personality' | 'romanceStyle' | 'relationship'
              ],
              {
                fan_name: bettieSettingInfo.userNickname,
                member_name: bettieSettingInfo.bettieNickname
              }
            )}
            description={
              isForeignUser
                ? MOOD_KEYWORDS_FOREIGN[
                    currentMoodKeyword as 'personality' | 'romanceStyle' | 'relationship'
                  ]
                : undefined
            }
            onTypingComplete={handleTypingComplete}
          />
        </div>
      </div>
      <div
        className="flex flex-col items-center gap-[10px] mt-[48px] mx-auto relative"
        style={{
          opacity: isTypingDone ? 1 : 0,
          transition: 'opacity 0.1s ease',
          pointerEvents: isTypingDone ? 'auto' : 'none'
        }}
      >
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 w-[166px] h-[48px] pointer-events-none"
          style={{
            background:
              colorMode === 'light'
                ? 'radial-gradient(8854.4% 78.01% at 50% 50%, rgba(203, 32, 255, 0.11) 0%, rgba(203, 32, 255, 0.00) 100%)'
                : 'radial-gradient(8854.4% 78.01% at 50% 50%, rgba(207, 51, 255, 0.18) 0%, rgba(203, 32, 255, 0.00) 100%)',
            filter: 'blur(19px)'
          }}
        />
        <p className="text-[12px] text-text-main-lv4 leading-[14px]">
          {t('BettieSetting.moodSuggestion.mostFrequent')}
        </p>
        <button
          type="button"
          className="text-[15px] font-medium leading-[18px] text-text-primary"
          style={{
            textDecoration: 'underline',
            textUnderlineOffset: '4px',
            textDecorationThickness: '2px'
          }}
          onClick={() => {
            const key = currentMoodKeyword as 'personality' | 'romanceStyle' | 'relationship';
            setMoodInput({ ...moodInput, [key]: suggestionMoodKeyword });
          }}
        >
          <span key={suggestionMoodKeyword} style={{ animation: 'fadeIn 0.4s ease' }}>
            {suggestionMoodKeyword}
          </span>
        </button>
      </div>
      <div className="px-[24px] mt-[25px] mb-[100px]">
        <InputBoxSingle
          className="bg-transparent"
          placeholder={t('BettieSetting.moodSuggestion.placeholder') as string}
          value={moodInput[currentMoodKeyword as 'personality' | 'romanceStyle' | 'relationship']}
          onChange={(e) => {
            const key = currentMoodKeyword as 'personality' | 'romanceStyle' | 'relationship';
            setMoodInput({ ...moodInput, [key]: e.target.value });
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={50}
        />
      </div>
      <div
        className={`fixed left-0 right-0 ${isKeyboardVisible ? 'bottom-[env(safe-area-inset-bottom)]' : 'px-[24px] bottom-[calc(10px+env(safe-area-inset-bottom))]'}`}
      >
        <div className="mb-[12px] flex items-center justify-center gap-[10px]">
          {Array.from({
            length: isChangeMood ? KEY_ORDER.length : KEY_ORDER.length + 1
          }).map((_, index) => (
            <CircleDot
              key={index.toString()}
              fillColor={
                index === KEY_ORDER.indexOf(currentMoodKeyword)
                  ? 'var(--color-text-main-lv3)'
                  : 'var(--color-separator2)'
              }
            />
          ))}
        </div>
        <Button
          size={isKeyboardVisible ? 'fill' : 'large'}
          className="w-full text-[17px] font-semibold"
          onClick={handleNextClick}
          disabled={
            !moodInput[currentMoodKeyword as 'personality' | 'romanceStyle' | 'relationship']
          }
        >
          {currentMoodKeyword === 'relationship' && isChangeMood ? t('done') : t('next')}
        </Button>
      </div>
    </div>
  );
};

export default BettieMoodSuggestion;
